import { IGenericTableData } from '@/interfaces/powerMarkets/table/IGenericTableData.ts';
import { IPlantActivity, IPlantActivityBoard } from '@/interfaces/powerMarkets/plant/IPlantActivity.ts';
import { IPlantsByCountry } from '@/interfaces/powerMarkets/plant/IPlantsByCountry.ts';
import {
    useApi, ApiPromise, TApiBaseUrlKey, liveTableEndpoints, apiAbortOptions,
} from '@/api/useApi.ts';
import { IPlantActivityPayload } from '@/interfaces/powerMarkets/payloads/IPlantActivityPayload.ts';
import { IPlantDetails, IEnactPlants } from '@/interfaces/powerMarkets/plant/IPlantDetails.ts';
import { IPlantInfo } from '@/interfaces/powerMarkets/plant/IPlantInfo.ts';
import { IPlantActivityDownloadPayload } from '@/interfaces/powerMarkets/payloads/IPlantActivityDownloadPayload.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';

const apiCall = useApi();
const staticApiCall = useApi('static');
const entsoeApiCall = useApi('entsoe');
const irelandApi = useApi('ireland');
const franceApi = useApi('france');

function getPlant (endpoint: TApiBaseUrlKey): ApiPromise<IPlantsByCountry> {
    const chosenApiByEndpoint = useApi(endpoint);

    return chosenApiByEndpoint.get<IPlantsByCountry>({
        endpoint: '/Plant',
    });
}

function getGbPlantPageData (endpoint: string): ApiPromise<IGenericTableData> {
    return apiCall.get<IGenericTableData>({
        endpoint: `/plantPage/${endpoint}`,
    });
}

function getNonGbPlantPageData (countryId: string): ApiPromise<IGenericTableData> {
    const selectedCountryEndpoint = liveTableEndpoints.find(x => x.id === countryId.toLowerCase());
    if (!selectedCountryEndpoint || selectedCountryEndpoint.plantTableFromEntsoe) {
        return entsoeApiCall.get<IGenericTableData>({
            endpoint: `/plantPage/${countryId}`,
        });
    }
    return useApi(selectedCountryEndpoint.endpointKey).get<IGenericTableData>({
        endpoint: '/plantPage/',
    });
}

function getPlantRegisterData (endpoint: string): ApiPromise<IGenericTableData> {
    return staticApiCall.get<IGenericTableData>({
        endpoint: `/plantStore/register/${endpoint}`,
    });
}

function getAllRelatedPlants (plantId: string, tableNo: number): ApiPromise<IEnactPlants> {
    return apiCall.get<IEnactPlants>({
        endpoint: `/plants/all-related-plants/${plantId}/${tableNo}`,
    });
}

function getByStoretrackId (storetrackId: number, tableNo: number): ApiPromise<Array<IPlantDetails>> {
    return apiCall.get<Array<IPlantDetails>>({
        endpoint: `/plants/get-by-storetrack-id/${storetrackId}/${tableNo}`,
    });
}

function getLastZeroPn (plantId: string): ApiPromise<string> {
    return apiCall.get<string>({
        endpoint: `/lastZeroPn/${plantId}`,
    });
}

function getPlantRemit (plantId: string, from: string, to: string): ApiPromise<IGenericTableData> {
    return apiCall.get<IGenericTableData>({
        endpoint: `/plantRemitSummary/${plantId}/${from}/${to}`,
    });
}

function getSemoPlantData (plantId: string): ApiPromise<IPlantInfo> {
    return irelandApi.get<IPlantInfo>({
        endpoint: `/Plant/Ireland/${plantId}`,
    });
}

function getEntsoePlantInfo (plantId: string, country: string): ApiPromise<IPlantInfo> {
    return entsoeApiCall.get<IPlantInfo>({
        endpoint: `/Plant/${country}/${plantId}`,
    });
}

function getFrancePlantData (plantId: string): ApiPromise<IPlantInfo> {
    return franceApi.get<IPlantInfo>({
        endpoint: `/Plant/${plantId}`,
    });
}

function getPlantActivity (): ApiPromise<Array<IIdName>> {
    return apiCall.get<Array<IIdName>>({
        endpoint: '/plantActivity',
    });
}

async function postPlantActivity (payload: IPlantActivityPayload, signal?: AbortSignal): Promise<IPlantActivity> {
    const response = await apiCall.post<IPlantActivityPayload, IPlantActivity>({
        endpoint: '/plantActivity',
        payload,
        ...apiAbortOptions(signal),
    });

    return response.data;
}

// TODO (BACKEND) - not ideal having same outgoing api call returning two different data types
async function postPlantActivityBoard (payload: IPlantActivityPayload, signal?: AbortSignal): Promise<IPlantActivityBoard> {
    const response = await apiCall.post<IPlantActivityPayload, IPlantActivityBoard>({
        endpoint: '/plantActivity',
        payload,
        ...apiAbortOptions(signal),
    });

    return response.data;
}

async function postDownloadPlantActivity (payload: IPlantActivityDownloadPayload): Promise<PlantActivityDownloadReturn> {
    const response = await apiCall.post<IPlantActivityDownloadPayload, PlantActivityDownloadReturn>({
        endpoint: '/plantActivity/download',
        payload,
    });

    return response.data;
}

export type PlantActivityDownloadReturn = (string | number)[][];

export const plantApi = {
    getGbPlantPageData,
    getLastZeroPn,
    getNonGbPlantPageData,
    getPlant,
    getPlantRegisterData,
    postPlantActivity,
    postPlantActivityBoard,
    postDownloadPlantActivity,
    getAllRelatedPlants,
    getPlantRemit,
    getSemoPlantData,
    getEntsoePlantInfo,
    getFrancePlantData,
    getPlantActivity,
    getByStoretrackId,
};
